import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';

/**
 * Brand color stacks available in the system.
 */
export type BrandColor = 'blue';

interface ThemeContextType {
  brand: BrandColor;
  setBrand: (brand: BrandColor) => void;
  availableBrands: { id: BrandColor; label: string; color: string }[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const AVAILABLE_BRANDS: {
  id: BrandColor;
  label: string;
  color: string;
}[] = [{ id: 'blue', label: 'Default Blue', color: '#465fff' }];

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Lock to blue
  const brand: BrandColor = 'blue';

  useEffect(() => {
    // Set data-brand attribute immediately and on state sync
    document.documentElement.dataset.brand = 'blue';
    localStorage.setItem('theme_brand', 'blue');
  }, []);

  const setBrand = useCallback((_newBrand: BrandColor) => {
    // No-op to prevent changes, keeping signature for compatibility
  }, []);

  const value = useMemo(
    () => ({
      brand,
      setBrand,
      availableBrands: AVAILABLE_BRANDS,
    }),
    [brand, setBrand]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
